import { Globe, ShieldCheck } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../contexts/LanguageContext";
import { AuthBrandingPanel } from "./AuthBrandingPanel";

const AuthFooter: React.FC = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const linkClass =
    "whitespace-nowrap transition-colors hover:text-slate-700 hover:underline";
  const dot = (
    <span className="select-none text-slate-300" aria-hidden="true">
      •
    </span>
  );

  return (
    <footer className="w-full border-t border-slate-200/80 pt-4 text-[11px] leading-relaxed text-slate-500 sm:text-xs xl:pt-6">
      <div className="flex items-center justify-between gap-y-3 max-sm:flex-col">
        <div className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600/80" />
          <span className="whitespace-nowrap">{t("Conexão Segura")}</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-1">
          <a
            href="https://virtusautomation.com/"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            &copy; {year} Virtus Automation
          </a>
          {dot}
          <a
            href="https://virtusautomation.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {t("Termos")}
          </a>
          {dot}
          <a
            href="https://virtusautomation.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {t("Privacidade")}
          </a>
        </div>
      </div>
    </footer>
  );
};

interface AuthLayoutProps {
  children: React.ReactNode;
}

/**
 * Casca das telas de autenticação: formulário à esquerda, painel de marca à direita (lg+).
 * As três faixas (topo / conteúdo / rodapé) espelham as do painel de marca.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  const { t, language, setLanguage } = useTranslation();

  return (
    <div className="flex h-dvh w-full overflow-hidden max-lg:flex-col bg-slate-50 text-slate-900 selection:bg-violet-500/20 selection:text-violet-700">
      <div className="flex h-full w-full flex-1 flex-col overflow-y-auto px-6 py-6 sm:px-10 sm:py-8 lg:w-1/2 lg:flex-initial xl:px-16 xl:py-10 2xl:py-12">
        <div className="mx-auto flex min-h-full w-full max-w-[420px] flex-col justify-between gap-6 [@media(max-height:900px)]:gap-2">
          <header className="flex h-8 w-full shrink-0 items-center justify-between sm:h-9 xl:h-10">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <Link
                to="/auth"
                className="inline-flex items-center rounded-lg p-0.5 transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
                aria-label={t("Virtus SMM — ir para o início")}
              >
                <img
                  src="/logo.png"
                  alt="Virtus"
                  className="h-7 w-auto object-contain sm:h-8"
                />
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setLanguage(language === "pt" ? "en" : "pt")}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-violet-500"
                title={
                  language === "pt"
                    ? "Switch to English"
                    : "Mudar para Português"
                }
                aria-label={
                  language === "pt"
                    ? "Switch to English"
                    : "Mudar para Português"
                }
              >
                <Globe className="h-3.5 w-3.5" />
                <span>{language === "pt" ? "PT" : "EN"}</span>
              </button>
            </div>
          </header>

          <main className="my-auto w-full bg-transparent! py-2 sm:py-4 [@media(max-height:900px)]:py-0">
            {children}
          </main>

          <div className="mt-auto w-full shrink-0">
            <AuthFooter />
          </div>
        </div>
      </div>

      <div className="hidden h-full border-l border-slate-200/80 lg:block lg:w-1/2">
        <AuthBrandingPanel />
      </div>
    </div>
  );
};
